"use client";

import Breadcrumb from "@/components/Common/Breadcrumb";
import { getImagePath } from "@/lib/utils";
import React from "react";
import Testimonials from "@/components/Testimonials";
import HowItWork from "@/components/HowItWorks";
import Benefits from "@/components/Benefits";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { formatNumberByLocale } from "@/utils/numberFormatter";

const Page = () => {
  const t = useTranslations();

  const items = t.raw("benefitsForProducer.items");
  // Two producer tracks (en/mr/hi only for now).
  const tracks = t.has("benefitsForProducer.tracks.title")
    ? (t.raw("benefitsForProducer.tracks.rows") as { label: string; newRelease: string; releasedFilm: string }[])
    : [];
  const locale = useLocale();

  return (
    <section className="section-padding bg-white py-24">
      <Breadcrumb
        pageName={t("benefitsForProducer.title")}
        description={t("benefitsForProducer.description")}
        bgImages={[getImagePath("/images/breadcrumb/howItWorks.webp")]}
        bgIndex={0}
        buttons={[
          {
            label: t("benefitsForProducer.buttons.joinNow"),
            href: "/join-producer",
            props: {
              variant: "default",
              className:
                "ml-2 rounded-tl-xl rounded-br-xl bg-white text-primary hover:bg-primary hover:text-white",
            },
          },
          {
            label: t("benefitsForProducer.buttons.calculator"),
            href: "/producer-revenue-calculator",
            props: {
              variant: "default",
              className:
                "ml-2 rounded-tl-xl rounded-br-xl bg-white text-primary hover:bg-primary hover:text-white",
            },
          },
        ]}
      />

      {/* PRODUCER TRACKS */}
      {tracks.length > 0 && (
        <div className="container mt-5">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#003399] mb-4">
            {t("benefitsForProducer.tracks.title")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-left text-base md:text-lg">
              <thead>
                <tr className="bg-[#003399] text-white">
                  <th className="p-3 md:p-4 font-semibold">{t("benefitsForProducer.tracks.col1")}</th>
                  <th className="p-3 md:p-4 font-semibold">{t("benefitsForProducer.tracks.newRelease")}</th>
                  <th className="p-3 md:p-4 font-semibold">{t("benefitsForProducer.tracks.releasedFilm")}</th>
                </tr>
              </thead>
              <tbody>
                {tracks.map((row, i) => (
                  <tr key={i} className="border-b border-gray-200 even:bg-gray-50">
                    <th className="p-3 md:p-4 font-semibold text-[#003399] align-top">{row.label}</th>
                    <td className="p-3 md:p-4 align-top">{row.newRelease}</td>
                    <td className="p-3 md:p-4 align-top">{row.releasedFilm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FIRST TWO ITEMS WITH IMAGE */}
      <div className="container grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center justify-center mt-5 ">
        <div>
          {items.slice(0, 2).map((item: any) => (
            <div key={item.id} className="flex items-start mb-10 gap-5">
              <h1 className="numbering text-[#003399]">
                {" "}
                {formatNumberByLocale(locale, item.id)}
              </h1>
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#003399] mb-4">
                  {item.heading}
                </h2>
                <ul className="list-item BenefitsPart list-inside text-lg text-body-color space-y-2">
                  {item.points.map((p: string, index: number) => (
                    <li key={index}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div>
          <img
            src={getImagePath("/images/benefits/produ.png")}
            alt="Benefits for Producers"
            className="w-full h-auto mx-auto"
          />
        </div>
      </div>

      {/* REMAINING ITEMS */}
      <div className="container grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-20 items-start justify-center mt-5 ">
        {items.slice(2).map((item: any) => (
          <div key={item.id} className="flex items-start gap-2">
            <h1 className="numbering text-[#003399]">
              {" "}
              {formatNumberByLocale(locale, item.id)}
            </h1>
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-[#003399] mb-4">
                {item.heading}
              </h2>
              <ul className="list-item BenefitsPart list-inside text-lg text-body-color space-y-2">
                {item.points.map((p: string, index: number) => (
                  <li key={index}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* JOIN BUTTON */}
      <div className="container mt-10 mb-24">
        <div className="text-center mt-10 pb-8 flex flex-col items-center relative">
          <p
            className="text-center 
            before:content-[''] before:block before:w-[40%] before:absolute before:left-0 before:top-[70%] before:h-0.5 before:bg-gray-300 before:-translate-y-8
          "
          ></p>

          <a
            href="/join-producer"
            className="bg-gradient-to-r z-10 from-[#003399] to-[#0099CC] text-white rounded-tl-xl rounded-br-xl inline-flex items-center justify-center px-4 py-2 text-sm font-medium"
          >
            {t("benefitsForProducer.buttons.joinNow")}
          </a>

          <p
            className="text-center 
            before:content-[''] before:block before:absolute before:right-0 before:top-[70%] before:w-[40%] before:h-0.5 before:bg-gray-300 before:-translate-y-8
          "
          ></p>
        </div>

        <div className="flex justify-center items-center mt-10 pb-8 relative w-full">
          <div className="w-full max-w-5xl">
            <Benefits benefitsToShow={[1, 3]} />
          </div>
        </div>
      </div>

      <HowItWork />
      {/* <Testimonials /> */}
    </section>
  );
};

export default Page;
